<template>
  <div class="tw:flex tw:flex-col tw:gap-2 tw:mt-4">
    <p class="heading-h4-size">{{ $t('diveBack') }}</p>
    <div ref="scrollWrapper" class="tw:flex tw:flex-col tw:gap-2">
      <div
        v-for="{ titleLocaleKey, descriptionLocaleKey, videoLocaleKey, animationKey, image, status } in eventsHistory"
        :key="titleLocaleKey"
        class="tw:gap-1 tw:p-4 card-link-icon game-event"
      >
        <div class="tw:flex tw:gap-4">
          <div class="tw:min-w-16">
            <img
              :src="isEventActive(status) && image ? image : eventImagePlaceholder"
              :width="$device.isMobile ? 72 : 80"
              alt=""
              class="image-2"
            />
          </div>
          <div class="tw:flex tw:flex-col tw:gap-2 tw:font-extrabold tw:text-white" @click="runAnimation(animationKey)">
            <h2 class="tw:text-lg tw:sm:text-2xl heading-h5-size">
              {{ $t(titleLocaleKey) }}
            </h2>
            <span
              :class="[
                ' event-type tw:border',
                {
                  'tw:border-[#7fdca4]! tw:text-white': isEventActive(status),
                  'tw:text-white/40': !isEventActive(status)
                }
              ]"
            >
              {{ getTagTitle(status, !!videoLocaleKey) }}
            </span>
            <p class="card-description">
              <span>{{ $t(descriptionLocaleKey) }}</span>
            </p>
          </div>
        </div>
        <img
          class="card-link-right-icon link-icon-right line-rounded-icon"
          src="/public/img/icons/play_video.svg"
          loading="lazy"
          @click="openVideo(titleLocaleKey, videoLocaleKey)"
        />
      </div>
    </div>
    <el-dialog
      v-model="showPopup"
      :fullscreen="false"
      :title="$t(videoTitle)"
      :append-to-body="true"
      class="video-popup"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      @close="closePopup"
    >
      <div class="video-wrapper">
        <iframe
          class="video-iframe"
          :src="`https://www.youtube.com/embed${$t(videoSlug)}`"
          :title="$t('historyModalYoutubePlayer')"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
        />
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
enum EVENT_STATUS {
  ACTIVE,
  INACTIVE
}

interface IEvent {
  titleLocaleKey: string;
  descriptionLocaleKey: string;
  status: EVENT_STATUS;
  image?: string;
  videoLocaleKey?: string;
  animationKey?: string;
}

const showPopup = ref(false);
const videoSlug = ref('');
const videoTitle = ref('');
const scrollWrapper = templateRef<HTMLElement>('scrollWrapper');

const { isShowStoryModal = false } = defineProps<{ isShowStoryModal?: boolean }>();

const emit = defineEmits<{
  runAnimation: [key: string];
}>();

function runAnimation(animationKey?: string) {
  if (!animationKey) return;
  showPopup.value = false;
  emit('runAnimation', animationKey);
}

const eventImagePlaceholder = '/img/pirate-avatar.jpg';

const eventsHistory: IEvent[] = [
  {
    titleLocaleKey: 'historyModalVideoTitleTortuga',
    descriptionLocaleKey: 'historyModalVideoDescriptionTortuga',
    image: '/img/historyModalVideoIcons/Story1.png',
    videoLocaleKey: 'historyModalVideoParamsTortuga',
    status: EVENT_STATUS.ACTIVE
  },
  {
    titleLocaleKey: 'historyModalVideoTitleHowSystemWorks',
    descriptionLocaleKey: 'historyModalVideoDescriptionHowSystemWorks',
    image: '/img/historyModalVideoIcons/Story2.png',
    videoLocaleKey: 'historyModalVideoParamsHowSystemWorks',

    status: EVENT_STATUS.ACTIVE
  },
  {
    titleLocaleKey: 'historyModalVideoTitleCalmBeforeStorm',
    descriptionLocaleKey: 'historyModalVideoDescriptionCalmBeforeStorm',
    image: '/img/historyModalVideoIcons/Story5.png',
    videoLocaleKey: 'historyModalVideoParamsCalmBeforeStorm',

    status: EVENT_STATUS.ACTIVE
  },
  {
    titleLocaleKey: 'historyModalAttackOnSpanishShip',
    descriptionLocaleKey: 'historyModalAttackOnSpanishShipDescription',
    status: EVENT_STATUS.ACTIVE,
    animationKey: 'Ship_battle'
  },
  {
    titleLocaleKey: 'historyModalVideoTitleBattleOver',
    descriptionLocaleKey: 'historyModalVideoDescriptionBattleOver',
    image: '/img/historyModalVideoIcons/Story6.png',
    videoLocaleKey: 'historyModalVideoParamsBattleOver',
    status: EVENT_STATUS.ACTIVE
  },
  {
    titleLocaleKey: 'historyModalVideoTitleLootSecured',
    descriptionLocaleKey: 'historyModalVideoDescriptionLootSecured',
    image: '/img/historyModalVideoIcons/Story4.png',
    videoLocaleKey: 'historyModalVideoParamsLootSecured',

    status: EVENT_STATUS.ACTIVE
  },

  {
    titleLocaleKey: 'historyModalPearlsTransported',
    descriptionLocaleKey: 'historyModalPearlsTransportedDescription',
    status: EVENT_STATUS.INACTIVE
  },
  {
    titleLocaleKey: 'historyModalPearlDiversHired',
    descriptionLocaleKey: 'historyModalPearlDiversHiredDescription',
    status: EVENT_STATUS.INACTIVE
  },
  {
    titleLocaleKey: 'historyModalExpeditionStarts',
    descriptionLocaleKey: 'historyModalExpeditionStartsDescription',
    status: EVENT_STATUS.INACTIVE
  },
  {
    titleLocaleKey: 'historyModalGoldMintingRenewed',
    descriptionLocaleKey: 'historyModalGoldMintingRenewedDescription',
    status: EVENT_STATUS.INACTIVE
  }
];

const isEventActive = (status: EVENT_STATUS) => status === EVENT_STATUS.ACTIVE;

useResetScroll(
  toRef(() => isShowStoryModal),
  scrollWrapper
);

function closePopup() {
  showPopup.value = false;
  videoSlug.value = '';
}

const { t } = useI18n();

const getTagTitle = (status: EVENT_STATUS, isVideo: boolean) => {
  if (status === EVENT_STATUS.ACTIVE) {
    return isVideo ? t('historyModalVideo') : t('historyModalEventTypeActive');
  }
  return t('historyModalEventTypeLocked');
};

function openVideo(title: string, localeKey?: string) {
  if (!localeKey) return;
  videoSlug.value = localeKey;
  videoTitle.value = title;
  showPopup.value = true;
}
</script>

<style lang="scss" scoped>
.card-description {
  font-weight: 400;
  font-size: 18px;
  color: #90989d;

  @media screen and (max-width: 479px) {
    font-size: 12px;
    line-height: 18px;
  }
}

.heading-h4-size {
  color: #90989d;
  font-family: Grenze, sans-serif;
  font-size: 24px;
  font-weight: 800;
  font-family: Grenze, sans-serif;
  text-align: center;
  margin: 0;

  @media (max-width: 450px) {
    font-size: 18px;
  }
}

.card-link-right-icon {
  color: #fff;
  font-family:
    Line Rounded Icons,
    sans-serif;
  font-size: 24px;
  line-height: 1em;
}

.button-arrow {
  transform: translate3d(0px, 0px, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg);
  transform-style: preserve-3d;
  transition: 300ms ease all;

  &:hover {
    transform: translate3d(-4px, 0px, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg);
  }
}

.closedBtn .button-arrow {
  transform: translate3d(0px, 0px, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(180deg) skew(0deg);
  transform-style: preserve-3d;

  &:hover {
    transform: translate3d(-4px, 0px, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(180deg) skew(0deg);
  }
}

.card-link-icon {
  transform-style: preserve-3d;
  background-color: #0a0a0a;
  border: 1px solid #2f2f2f;
  border-radius: 12px;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  transition:
    background-color 0.3s,
    transform 0.3s,
    border-color 0.3s,
    color 0.3s;
  display: flex;
  cursor: pointer;
  line-height: 1.6em;

  &:hover {
    background-color: #161616;
    border-color: #2f2f2f;
    transform: scale3d(0.98, 0.98, 1.01);
  }
}

.card-link-icon.game-event {
  color: rgba(255, 255, 255, 0.4);
  background-color: rgba(47, 47, 47, 0.4);
  transition:
    background-color 0.3s,
    transform 0.3s,
    border-color 0.3s;

  &:hover {
    color: rgba(255, 255, 255, 0.4);
    background-color: rgba(47, 47, 47, 0.8);
  }
}

.line-rounded-icon.link-icon-right.card-link-right-icon {
  @media screen and (max-width: 767px) {
    font-size: 20px;
  }
}

.event-type {
  text-align: center;
  border-radius: 8px;
  min-width: 105px;
  width: fit-content;
  padding: 2px 10px;
  font-size: 12px;
  line-height: 150%;
}

.card-link-icon.game-event.active {
  background-color: rgba(31, 51, 56, 0.4);
  border-color: #14282e;
}

.image-2 {
  border-radius: 8px;
  width: 80px;

  @media screen and (max-width: 479px) {
    width: 72px;
  }
}

.video-wrapper {
  position: relative;
  padding-bottom: calc(56.25% * 0.75); /* 16:9 */
  height: 0;
  text-align: center;
  margin: auto;
}

.video-wrapper iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
